import { DOCUMENT } from '@angular/common';
import { Inject, Injectable, Optional } from '@angular/core';
import { SHADOW_ROOT_TOKEN } from '@client/mfe/tokens/mfe-tokens';

type PrimeNgUseStyleOptions = {
    immediate?: boolean;
    manual?: boolean;
    name?: string;
    id?: string;
    media?: string;
    nonce?: string;
    first?: boolean;
    props?: Record<string, string>;
};

@Injectable()
export class ShadowDomPrimeNgUseStyleService {
    private styleId = 0;

    constructor (
        @Inject(DOCUMENT) private readonly document: Document,
        @Optional() @Inject(SHADOW_ROOT_TOKEN) private readonly shadowRoot: ShadowRoot | null,
    ) {}

    use (css: string, options: PrimeNgUseStyleOptions = {}): {
        id: string | undefined;
        name: string;
        el: HTMLStyleElement;
        css: string;
    } | undefined {
        const {
            immediate = true,
            manual = false,
            name = `style_${++this.styleId}`,
            id,
            media,
            nonce,
            first = false,
            props = {},
        } = options;

        void immediate;
        void manual;
        void props;

        const styleHost = this.shadowRoot ?? this.document.head;
        if (!styleHost) {
            return;
        }

        const transformedCss = this.transformCssForStyleHost(css, name, styleHost);

        const styleRef = styleHost.querySelector<HTMLStyleElement>(`style[data-primeng-style-id="${name}"]`) ??
            this.findStyleById(styleHost, id) ??
            this.document.createElement('style');

        if (!styleRef.isConnected) {
            if (first && styleHost.firstChild) {
                styleHost.insertBefore(styleRef, styleHost.firstChild);
            } else {
                styleHost.appendChild(styleRef);
            }

            this.setStyleAttributes(styleRef, {
                type: 'text/css',
                media,
                nonce,
                'data-primeng-style-id': name,
            });
        }

        if (styleRef.textContent !== transformedCss) {
            styleRef.textContent = transformedCss;
        }

        return {
            id,
            name,
            el: styleRef,
            css: transformedCss,
        };
    }

    private transformCssForStyleHost (css: string, name: string, styleHost: ShadowRoot | HTMLHeadElement): string {
        if (styleHost !== this.shadowRoot || !this.isPrimeNgVariableStyle(name, css)) {
            return css;
        }

        // PrimeNG emits runtime token sheets under `:root`, which does not scope
        // correctly once the style tag is injected into the MFE shadow root.
        // Re-anchor those custom properties to the web component host so they
        // cascade through the entire shadow tree.
        return css.replace(/:root\b/g, ':host');
    }

    private isPrimeNgVariableStyle (name: string, css: string): boolean {
        return name.endsWith('-variables') &&
            css.includes(':root') &&
            css.includes('--p-');
    }

    private findStyleById (styleHost: ShadowRoot | HTMLHeadElement, id?: string): HTMLStyleElement | null {
        if (!id) {
            return null;
        }

        if ('getElementById' in styleHost && typeof styleHost.getElementById === 'function') {
            return styleHost.getElementById(id) as HTMLStyleElement | null;
        }

        return this.document.getElementById(id) as HTMLStyleElement | null;
    }

    private setStyleAttributes (
        styleRef: HTMLStyleElement,
        attributes: Record<string, string | undefined>,
    ): void {
        for (const [name, value] of Object.entries(attributes)) {
            if (value === undefined) {
                continue;
            }

            styleRef.setAttribute(name, value);
        }
    }
}
